import type {
  ConflictMergeConditionPayload,
  JSONAPIPullRequestPayload,
  PullRequestMergeRequirementsPayload,
  StackMergeConditionPayload,
} from '../../page-data/payloads/merge-box'
import {BaseSectionStatus} from './base-section-status'
import {getStackCondition} from '../json-api-helpers'

export const Status = {
  CurrentEntryStackMergeConflicts: 'CurrentEntryStackMergeConflicts',
  StackMergeConflicts: 'StackMergeConflicts',
  StackRebaseRequired: 'StackRebaseRequired',
  StackRebaseOptional: 'StackRebaseOptional',
} as const

export type Status = (typeof Status)[keyof typeof Status]

export class StacksMergeConflictsAndRebaseSectionStatus extends BaseSectionStatus<Status | undefined> {
  readonly #mergeRequirements: PullRequestMergeRequirementsPayload | null
  readonly #stackCondition: StackMergeConditionPayload | undefined
  readonly #isStacked: boolean

  constructor(pullRequest: JSONAPIPullRequestPayload, mergeRequirements: PullRequestMergeRequirementsPayload | null) {
    super(pullRequest, mergeRequirements, undefined, {})
    this.#mergeRequirements = mergeRequirements
    this.#stackCondition = getStackCondition(this.#mergeRequirements)
    this.#isStacked = this.#stackCondition?.isStacked ?? false
  }

  override get sectionStatus(): Status | undefined {
    if (!this.#isStacked) return
    if (this.#currentEntryHasMergeConflicts) return Status.CurrentEntryStackMergeConflicts
    if (this.#stackHasMergeConflicts) return Status.StackMergeConflicts
    if (this.#stackHasRequireRebase) return Status.StackRebaseRequired
    if (this.#stackHasOptionalRebase) return Status.StackRebaseOptional

    return
  }

  get currentStackEntryMergeConflictsFailure(): ConflictMergeConditionPayload | undefined {
    if (!this.#isStacked) return undefined

    const condition = this.#mergeRequirements?.conditions.find(c => {
      return c.type === 'PULL_REQUEST_MERGE_CONFLICT_STATE' && c.result === 'FAILED'
    })

    return condition?.type === 'PULL_REQUEST_MERGE_CONFLICT_STATE' ? condition : undefined
  }

  get #stackHasRequireRebase(): boolean {
    return (this.#stackCondition?.entries ?? []).some(entry => {
      if (entry.position === 'AFTER') return false

      return entry.status === 'NEEDS_REBASE'
    })
  }

  get #currentEntryHasMergeConflicts(): boolean {
    const currentEntry = this.#stackCondition?.entries.find(entry => entry.position === 'CURRENT')
    return currentEntry?.status === 'MERGE_CONFLICTS'
  }

  get #stackHasMergeConflicts(): boolean {
    return (this.#stackCondition?.entries ?? []).some(entry => {
      if (entry.position === 'AFTER') return false

      return entry.status === 'MERGE_CONFLICTS'
    })
  }

  get #stackHasOptionalRebase(): boolean {
    return this.pullRequest.anyStackBranchOutdated
  }
}
